import { Component, OnInit, ViewEncapsulation } from "@angular/core";
import { Observable } from "rxjs";
import { PharmaDataService } from "../pharma-data.service";
import { ActivatedRoute } from "@angular/router";
import { NoDataMsg, phaseAbbreviations } from "../pharma-page.model";
import {
  VAIconRegistryService,
  chevronDown,
  collapseArrows,
  expandArrows,
  infoCircle,
} from "@va/core-library/icons";

import { faFileSearch } from "@fortawesome/pro-solid-svg-icons";
import { AppState } from "src/app/store/state/app.state";
import { Store } from "@ngrx/store";
import { DeleteDpParams } from "src/app/store/actions/global-subheader.actions";
@Component({
  selector: "app-drug-summary",
  templateUrl: "./drug-summary.component.html",
  styleUrls: ["./drug-summary.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class DrugSummaryComponent implements OnInit {
  faFileSearch = faFileSearch;
  loading = true;
  dataObservable: Observable<any>;
  data: any;
  abbreviations = phaseAbbreviations;
  visible: { [phase: string]: boolean } = {};
  moaDataVisible: boolean;
  targetDataVisible: boolean;
  apiDataVisible: boolean;
  auditIconVisible = {
    indications: false,
    moa: false,
    target: false,
  };
  options = [];
  phaseAbbr;
  values: any[] | null = null;
  cTagId: number = null;
  groupedIndications: any[] = [];
  customSkeletonConfig = {
    skeletonType: "list",
    config: {
      list: {
        noOfListItems: 3,
        styleUl: {
          height: "152px",
        },
      },
    },
  };
  errorMessage = NoDataMsg;

  constructor(
    private service: PharmaDataService,
    private route: ActivatedRoute,
    private iconRegistryService: VAIconRegistryService,
    private store: Store<AppState>,
  ) {
    this.iconRegistryService.registerIcons([
      chevronDown,
      collapseArrows,
      expandArrows,
      infoCircle,
    ]);
  }

  ngOnInit() {
    this.route.params.subscribe((param) => {
      this.cTagId = parseInt(param.cTagId, 10);
    });
    this.dataObservable = this.service.getDrugOverallSummary(this.cTagId);
    this.phaseAbbr = phaseAbbreviations;
    this.dataObservable.subscribe((resp) => {
      if (resp.data["indication_by_phase"].length > 0) {
        this.options.push({
          value: "indication_by_phase",
          label: `Peers by Indication (${resp.data["indication_by_phase"].length})`,
          children: [],
        });
      }
      if (resp.data["moa"].length > 0) {
        this.options.push({
          value: "moa",
          label: `Peers by MOA (${resp.data["moa"].length})`,
          children: [],
        });
      }
      if (resp.data["api"].length > 0) {
        this.options.push({
          value: "api",
          label: `Peers by API (${resp.data["api"].length})`,
          children: [],
        });
      }
      if (resp.data["target"].length > 0) {
        this.options.push({
          value: "target",
          label: `Peers by Target (${resp.data["target"].length})`,
          children: [],
        });
      }
      this.options.forEach((peerId) => {
        if (peerId["value"] === "indication_by_phase") {
          resp.data[peerId["value"]].forEach((indPhase) => {
            peerId["children"].push({
              value: resp.data.values_mtag_map[indPhase.indication],
              label: `<span>
              <span class="phase-pill" title="${indPhase["phase"]}">${
                this.phaseAbbr[indPhase["phase"]]
              }</span>
              ${indPhase["indication"]}
            </span>`,
              isLeaf: true,
              title: `${indPhase["indication"]}`,
            });
          });
        } else {
          resp.data[peerId["value"]].forEach((value) => {
            peerId["children"].push({
              value: resp.data.values_mtag_map[value],
              label: value,
              isLeaf: true,
            });
          });
        }
      });
    });
  }
  onChanges(event): void {
    let key = event[0];
    const val = event[1];
    if (key === "indication_by_phase") {
      key = "indication";
    }
    window.open(`/biopharma/database/filter?${key}=${val}`, "_blank");
    this.values = [];
  }
  // Toggle dropdown visibility for a specific phase
  toggleDropdown(phase: string) {
    this.visible[phase] = !this.visible[phase];
  }
  // Handle dropdown visibility change
  onMenuVisibleChange(visible: boolean, phase: string) {
    this.visible[phase] = visible;
  }
  onApiVisibleChange(visible) {
    this.apiDataVisible = visible;
  }
  onMoaVisibleChange(visible) {
    this.moaDataVisible = visible;
  }
  onTargetVisibleChange(visible) {
    this.targetDataVisible = visible;
  }
  onDrugSummaryDataResolved(resp) {
    this.data = resp;
    this.data.data.api.sort();
    this.data.data.moa.sort();

    //It is guranteed that the indications are sorted by phase in the api response. We group them in a single loop
    const indicationData = this.data.data.indication_by_phase;
    this.groupedIndications = [];
    if (indicationData.length) {
      let currentPhase = "";

      for (let i = 0; i < indicationData.length; i++) {
        const indData = indicationData[i];
        if (currentPhase !== indData.phase) {
          currentPhase = indData.phase;
          this.groupedIndications.push({
            phase: currentPhase,
            indications: [],
            count: 0,
          });
        }

        const cachedPhase =
          this.groupedIndications[this.groupedIndications.length - 1];
        cachedPhase.indications.push(indData.indication);
        cachedPhase.count = cachedPhase.count + 1;

        this.groupedIndications[this.groupedIndications.length - 1] =
          cachedPhase;
      }
    }

    this.auditSource({}, "indications", false);
    this.auditSource({}, "moa", false);
    this.auditSource({}, "target", false);
  }

  onPeers(peerId) {
    this.service.logUserAction(
      "GENERATE_PEERS",
      {
        peer: peerId,
        drugCtag: this.cTagId,
      },
      "BIOPHARMA_DRUG_PAGE",
    );
    window.open(
      `/biopharma/database/peers?peerId=${peerId}&ctagId=${this.cTagId}`,
      "_blank",
    );
  }

  auditSource(element, colName, isChangeDataPoint) {
    if (isChangeDataPoint && !this.auditIconVisible[colName]) {
      return;
    }
    const selectedCell = `${this.cTagId.toString()}|${colName}`; //instead of dapparameter we pass ctag id
    const newElement = JSON.parse(JSON.stringify(element));
    let tempColName = "";
    if (colName === "indications") tempColName = "indication_by_phase";
    else tempColName = colName;
    newElement["ctag"] = this.cTagId;
    newElement["dataObj"] = {
      [colName]: this.data.data[tempColName].map((item) => {
        let obj = {};
        obj["name"] =
          item[colName === "indications" ? "indication" : colName] ?? item;
        if (typeof item === "object") obj = { ...obj, ...item };
        return obj;
      }),
    };
    this.service
      .getAuditTrail(this.cTagId, colName, newElement["dataObj"][colName])
      .subscribe((resp) => {
        const auditTrailResponse = resp;
        if (!auditTrailResponse["source_not_available"]) {
          this.auditIconVisible[colName] = true;
        }

        if (isChangeDataPoint) {
          this.service.changeDataPoint(
            newElement,
            selectedCell,
            auditTrailResponse,
          );
        }
      });
    this.store.dispatch(new DeleteDpParams());
  }
}
